import {User} from '@hconnect/apiclient'
import {trackEvent, trackEventWithBrowserProps} from '@hconnect/common/logging/Analytics'
import {Button} from '@material-ui/core'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import React from 'react'
import {useHistory} from 'react-router-dom'

import {api} from '../../../App.store'
import {generateEventTrackBodyFromUser} from '../../../common/EventTracking/utils'
import {useTranslation} from '../../../hooks/useTranslation'

type UseDeleteUserProps = {
  setShowDeleteUserDialog: (showDialog: boolean) => void
  userData?: User
  shouldGoBack?: boolean
}

const deleteUser = async (userId: string) => {
  const response = await api.delete(`/users/${userId}`)
  return response.data
}

export const useDeleteUser = ({
  setShowDeleteUserDialog,
  userData,
  shouldGoBack = true
}: UseDeleteUserProps) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar()
  const {t} = useTranslation()
  const history = useHistory<{source: string}>()

  const mutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      setShowDeleteUserDialog(false)

      if (userData) {
        trackEventWithBrowserProps('adminUserDelete', generateEventTrackBodyFromUser(userData))
      }

      shouldGoBack ? history.goBack() : window.location.reload()
      enqueueSnackbar(
        t('manageUser.deleteUserSnackbarSuccess', {
          name: userData?.name
        }),
        {
          // eslint-disable-next-line react/display-name
          action: () => (
            <Button onClick={() => closeSnackbar()} variant="text" color="primary">
              {t('manageUser.deleteUserSnackbarClose')}
            </Button>
          ),
          variant: 'success'
        }
      )
    },
    onError: (e: AxiosError) => {
      setShowDeleteUserDialog(false)

      console.error(e)
      trackEvent('adminConsoleError', {
        product: 'adminConsole',
        date: new Date().toISOString(),
        errorCode: e.response?.status,
        component: 'ManageUser.tsx Delete user',
        endpoint: e.response?.config?.url
      })
      enqueueSnackbar(
        e.response?.status === 400 || e.response?.status === 403
          ? t('manageUser.hasNoPermissions')
          : t('manageUser.deleteUserSnackbarError'),
        {variant: 'error'}
      )
    }
  })

  const handleDeleteUser = () => {
    if (userData) {
      mutation.mutate(userData.id)
    }
  }

  return {
    handleDeleteUser,
    isDeleting: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  }
}
